import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormHeader } from '../components/FormHeader';
import { BreadcrumbItem } from '../components/Breadcrumb';
import { ServiceSwitcher, ServiceSwitcherOption } from '../components/ServiceSwitcher';
import { LanguageSelector } from '../components/LanguageSelector';
import { OverviewTab } from '../components/analytics/OverviewTab';
import { TechniciansTab } from '../components/analytics/TechniciansTab';
import { CustomersMachinesTab } from '../components/analytics/CustomersMachinesTab';
import { SparePartsTab } from '../components/analytics/SparePartsTab';
import { WorkflowTab } from '../components/analytics/WorkflowTab';
import { FailuresByFamilyTab } from '../components/analytics/FailuresByFamilyTab';
import { DataNotesTab } from '../components/analytics/DataNotesTab';
import { authService } from '../services/authService';
import './AnalyticsPage.css';

const FIRST_YEAR = 2021;

type AnalyticsTab = 'overview' | 'technicians' | 'customersMachines' | 'spareParts' | 'workflow' | 'failuresByFamily' | 'dataNotes';

const TABS: { id: AnalyticsTab; labelKey: string }[] = [
  { id: 'overview', labelKey: 'analytics.tabs.overview' },
  { id: 'technicians', labelKey: 'analytics.tabs.technicians' },
  { id: 'customersMachines', labelKey: 'analytics.tabs.customersAndMachines' },
  { id: 'spareParts', labelKey: 'analytics.tabs.spareParts' },
  { id: 'workflow', labelKey: 'analytics.tabs.workflow' },
  { id: 'failuresByFamily', labelKey: 'analytics.tabs.failuresByFamily' },
  { id: 'dataNotes', labelKey: 'analytics.tabs.dataNotes' },
];

const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');

  const canAccessAnalytics = currentUser?.scope === 'GLOBAL' && currentUser?.analyticsEnabled === true;
  const canAccessPassport = currentUser?.passportEnabled === true;

  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = currentYear; y >= FIRST_YEAR; y--) list.push(y);
    return list;
  }, [currentYear]);

  if (!canAccessAnalytics) {
    return <Navigate to="/" replace />;
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '', translateKey: 'breadcrumb.analytics' },
  ];

  // Same comparison rule as the backend's AnalyticsMetaDto (SPEC D4): current year -> same period
  // of the previous year; closed years -> full 12 months of the previous year; FIRST_YEAR -> no comparison.
  const comparisonYear = year <= FIRST_YEAR ? null : year - 1;
  const isSamePeriod = year === currentYear;
  // "Failures by family" always covers the whole period: the year selector and the year-over-year comparison do not apply.
  const followsYearSelector = activeTab !== 'failuresByFamily';

  const switcherOptions: ServiceSwitcherOption[] = [
    {
      id: 'service',
      label: t('serviceSwitcher.serviceReport'),
      logo: '/proservice logo.jpg',
      active: false,
      onSelect: () => navigate('/'),
    },
    ...(canAccessPassport
      ? [
          {
            id: 'passport',
            label: t('serviceSwitcher.passportService'),
            logo: '/passport logo.jpg',
            active: false,
            onSelect: () => navigate('/'),
          },
        ]
      : []),
  ];

  return (
    <div className="analytics-page">
      <FormHeader breadcrumbItems={breadcrumbItems}>
        <div className="analytics-header-actions">
          <label htmlFor="analytics-year-select" className="analytics-year-label">
            {t('analytics.yearLabel')}
          </label>
          <select
            id="analytics-year-select"
            className="analytics-year-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            disabled={!followsYearSelector}
            title={followsYearSelector ? undefined : t('analytics.yearNotApplicable')}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <div className="analytics-header-right">
            <ServiceSwitcher
              visible
              currentLabel={t('serviceSwitcher.analyticsService')}
              currentLogo="/analytics logo.jpeg"
              options={switcherOptions}
            />
            <LanguageSelector />
          </div>
        </div>
      </FormHeader>

      <nav className="analytics-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            id={`analytics-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            className={`analytics-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey)}
          </button>
        ))}

        <div className="analytics-tabs-badges">
          {followsYearSelector && comparisonYear !== null && (
            <span className="analytics-badge">
              {t('analytics.comparisonBadge', {
                year: comparisonYear,
                period: t(isSamePeriod ? 'analytics.comparisonSamePeriod' : 'analytics.comparisonFullYear'),
              })}
            </span>
          )}
          <span className="analytics-badge">{t('analytics.reportsIncludedBadge')}</span>
        </div>
      </nav>

      <main className="analytics-content">
        {TABS.map((tab) => (
          <section
            key={tab.id}
            role="tabpanel"
            hidden={activeTab !== tab.id}
            aria-labelledby={`analytics-tab-${tab.id}`}
          >
            {activeTab === tab.id && (
              <>
                {tab.id === 'overview' && <OverviewTab year={year} />}
                {tab.id === 'technicians' && <TechniciansTab year={year} />}
                {tab.id === 'customersMachines' && <CustomersMachinesTab year={year} />}
                {tab.id === 'spareParts' && <SparePartsTab year={year} />}
                {tab.id === 'workflow' && <WorkflowTab year={year} />}
                {tab.id === 'failuresByFamily' && <FailuresByFamilyTab />}
                {tab.id === 'dataNotes' && <DataNotesTab />}
              </>
            )}
          </section>
        ))}
      </main>
    </div>
  );
};

export default AnalyticsPage;
